import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Banner, Box, Card, H2, Separator, Text, VStack } from '@betterment/samba-react';
import { CardLoader, ReturnsText, useFeatureFlag } from '@betterment/shared-components';

import {
  DirectTradingAccountSecurityPositionsDocument,
  DirectTradingAccountSecurityPositionsQuery,
} from './graphql/directTradingAccountSecurityPositions.generated';
import { OrderDetailsLink } from './OrderDetailsLink';

import { Props } from '../../AccountSecurityPage';

type SecurityPosition = DirectTradingAccountSecurityPositionsQuery['account']['securityPosition'];

const EmptyValueText = () => (
  <Text variant="text50" color="text.weak">
    —
  </Text>
);

const SecurityPositionDetails: React.FC<{ securityPosition: NonNullable<SecurityPosition> }> = props => {
  const { securityPosition } = props;
  const { shares, amount, totalCost, averageCost, totalReturn, todaysReturn } = securityPosition;

  return (
    <>
      <VStack css={{ gap: 'space.300' }}>
        <VStack>
          <Text role="cell" variant="text50" color="text.weak">
            Market value
          </Text>
          <Text variant="text300">{formatMoney(amount)}</Text>
          <Text variant="text50" color="text.weak">
            {shares} shares
          </Text>
        </VStack>
        <Box
          css={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 'space.200', alignItems: 'start' }}
          role="table"
          aria-label="fundamentals"
        >
          <VStack>
            <Text variant="text50" color="text.weak">
              Avg share cost
            </Text>
            {averageCost ? (
              <Text variant="text50" color="text">
                {formatMoney(averageCost)}
              </Text>
            ) : (
              <EmptyValueText />
            )}
          </VStack>
          <VStack>
            <Text variant="text50" color="text.weak">
              Total cost
            </Text>
            {totalCost ? (
              <Text variant="text50" color="text">
                {formatMoney(totalCost)}
              </Text>
            ) : (
              <EmptyValueText />
            )}
          </VStack>
          <VStack>
            <Text variant="text50" color="text.weak">
              Total gain/loss
            </Text>
            {totalReturn ? (
              <ReturnsText amount={totalReturn.amount} percent={totalReturn.percent} />
            ) : (
              <EmptyValueText />
            )}
          </VStack>
          <VStack>
            <Text variant="text50" color="text.weak">
              Today's gain/loss
            </Text>
            {todaysReturn ? (
              <ReturnsText amount={todaysReturn.amount} percent={todaysReturn.percent} />
            ) : (
              <EmptyValueText />
            )}
          </VStack>
        </Box>
      </VStack>
    </>
  );
};

const PositionInfoBanner = ({ securityPosition }: { securityPosition?: SecurityPosition }) => {
  const { enabled: isCancelOrderEnabled } = useFeatureFlag({
    splitName: 'retail.self_directed_after_hours_order_cancellation_q4_2025_enabled',
    context: 'security_position_details',
  });

  if (!securityPosition) return null;

  const { hasPendingOrders, hasPendingCostBasis, pendingOrders } = securityPosition;

  const text = (() => {
    if (hasPendingOrders) return 'Order pending';
    if (hasPendingCostBasis) {
      return 'Cost basis from a recent transfer is pending. This missing data will impact cost and gain/loss data.';
    }
    return null;
  })();

  if (!text) return null;

  const firstPendingOrder = pendingOrders[0];

  return (
    <Banner variant="info" css={{ marginBottom: 'space.200' }}>
      <Banner.Content>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text as="p">{text}</Text>
          {firstPendingOrder && isCancelOrderEnabled && <OrderDetailsLink pendingOrder={firstPendingOrder} />}
        </Box>
      </Banner.Content>
    </Banner>
  );
};

export const SecurityPositionDetailsCard: React.FC<Props> = ({ accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSecurityPositionsDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { securityPosition } = data.account;

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header>
        <H2 variant="title200">Your holdings</H2>
      </Card.Header>
      <Separator />
      <Card.Content css={{ gap: 'space.50', display: 'grid' }}>
        <PositionInfoBanner securityPosition={securityPosition} />
        {securityPosition && securityPosition.shares > 0 ? (
          <SecurityPositionDetails securityPosition={securityPosition} />
        ) : (
          <Text>Purchased securities will show here.</Text>
        )}
      </Card.Content>
    </Card>
  );
};
